<script setup lang="ts">
import type { TableOptions } from '@core/stores/user'
import { useUserStore } from '@core/stores/user'
import type { SortItem } from '@core/types'
import { useChatStore } from '@/views/apps/chat/useChatStore'
import type { NonEmptyArray } from '@/utils/helpers'
import UserAddEditDialog from '@/components/dialogs/UserAddEditDialog.vue'
import UsersRoleSummary from '@/views/users/UsersRoleSummary.vue'
import { getDateLocale, getFlatpickrLocale } from '@core/utils/locale'
import { Modules } from '@/types/generated'
import LastLoginFilterEnum = Modules.User.Enums.LastLoginFilterEnum
import UserCustomFieldTypeEnum = Modules.UserCustomField.Enums.UserCustomFieldTypeEnum

type UserCustomFieldData = Modules.UserCustomField.UserCustomFieldData

definePage({
  meta: {
    action: 'read',
    subject: 'users',
  },
})

const { t } = useI18n()
const table = 'users'
const userStore = useUserStore()
const userData = useCookie<any>('userData')
const chatStore = useChatStore()

const userRoles: string [] = userData.value?.roles ?? []

const router = useRouter()
const ability = useAbility()

const headers = [
  { title: t(`${table}-page.user`), key: 'user' },
  { title: t(`${table}-page.roles`), key: 'roles', sortable: false },
  { title: t(`${table}-page.effectiveness`), key: 'effectiveness' },
  { title: t(`${table}-page.cases_progress`), key: 'cases_progress', sortable: false },
  { title: t(`${table}-page.department`), key: 'department' },
  { title: t(`${table}-page.position`), key: 'position' },
  { title: t(`${table}-page.last_login_at`), key: 'last_login_at' },
  { title: t(`${table}-page.status`), key: 'status' },
  { title: t(`${table}-page.actions`), key: 'actions', sortable: false },
]

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'asc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const useLoading = ref<boolean>(false)
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const itemForDelete = ref()
const currenItem = ref()
const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isImportDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)
const isSnackbarVisible = ref(false)
const snackbarText = ref('')

const notifyError = (text: string) => {
  snackbarText.value = text
  isSnackbarVisible.value = true
}

// Плашка-чип и селект «Роль» пишут в одно и то же состояние: у него единственный
// вход — сеттер ниже, поэтому разъехаться они не могут. Страницу сбрасываем прямо
// в сеттере, а не в watch: watch отработал бы после useApi, и фильтр успел бы уйти
// запросом со старым `page`. Остальные фильтры устроены так же.
const roleState = ref<string | null>(currentTableOptions.role ?? null)
const activityState = ref<string | null>(currentTableOptions.activity ?? null)
const activityFromState = ref<string | null>(currentTableOptions.activityFrom ?? null)
const customFieldState = ref<string | null>(currentTableOptions.customFieldId ?? null)
const customFieldOptionState = ref<string | null>(currentTableOptions.customFieldOptionId ?? null)
const lastLoginState = ref<LastLoginFilterEnum | null>(currentTableOptions.lastLogin ?? null)
const lastLoginFromState = ref<string | null>(currentTableOptions.lastLoginFrom ?? null)

function filterModel<T extends string>(state: Ref<T | null>, onChange?: () => void) {
  return computed<T | null>({
    get: () => state.value,
    set: value => {
      // AppDateTimePicker при очистке отдаёт пустую строку — для фильтра это «не задан».
      const normalized = value || null

      if (normalized === state.value)
        return

      state.value = normalized
      onChange?.()
      page.value = 1
    },
  })
}

const roleFilter = filterModel(roleState)
const activityFilter = filterModel(activityState)
const activityFromFilter = filterModel(activityFromState)

// Опция прежнего поля к новому не относится — бэкенд ответил бы 422.
const customFieldFilter = filterModel(customFieldState, () => {
  customFieldOptionState.value = null
})

const customFieldOptionFilter = filterModel(customFieldOptionState)
const lastLoginFilter = filterModel(lastLoginState)
const lastLoginFromFilter = filterModel(lastLoginFromState)

// ufo выбрасывает из query-строки только `undefined`; `null` дал бы `?role`,
// который бэкенд прочитает как пустую строку, а не как «фильтр не задан».
const roleQuery = computed(() => roleFilter.value ?? undefined)
const activityQuery = computed(() => activityFilter.value ?? undefined)

// Без значения «Проходил сценарии» бэкенд дату игнорирует — не шлём её, чтобы не менять URL впустую.
const activityFromQuery = computed(() => (activityFilter.value !== null ? activityFromFilter.value ?? undefined : undefined))

// Поле и опция уходят только парой: одно без другого бэкенд отклоняет (422).
const customFieldQuery = computed(() => (customFieldOptionFilter.value !== null ? customFieldFilter.value ?? undefined : undefined))
const customFieldOptionQuery = computed(() => (customFieldFilter.value !== null ? customFieldOptionFilter.value ?? undefined : undefined))

// «Заходил с даты» без даты — тоже 422: пока дату не выбрали, режим не шлём.
const isLastLoginSince = computed(() => lastLoginFilter.value === LastLoginFilterEnum.Since)
const lastLoginQuery = computed(() => (isLastLoginSince.value && lastLoginFromFilter.value === null ? undefined : lastLoginFilter.value ?? undefined))
const lastLoginFromQuery = computed(() => (isLastLoginSince.value ? lastLoginFromFilter.value ?? undefined : undefined))

// URL — неизменяемая строка, поэтому `refetch: true` в useApi её не перезапрашивает:
// счётчики берутся ровно один раз при монтировании и не зависят от поиска и фильтров.
// Запрос стоит ДО запроса списка намеренно — см. проверку роли ниже.
const { data: roleCountsData } = await useApi<Modules.User.UsersRoleCountsData>(`/${table}/role-counts`)

const roleCounts = computed(() => roleCountsData.value ?? { total: 0, counts: {} })

// Роль могла исчезнуть из счётчиков, пока cookie её хранила (последнего `head`
// перевели в другую роль). Плашки и селект строятся из `counts`, поэтому такой фильтр
// не показывался бы нигде, а список бы резал — снять его штатно было бы нечем.
// Чиним до того, как значение попадёт в query: реакция на уже собранный URL стоила бы
// второго запроса списка. Пишем в `roleState` мимо сеттера — тот сбросил бы ещё и
// `page`, а это не смена фильтра пользователем, а починка при загрузке. Счётчики не
// доехали (`null`) — не трогаем: отсутствие данных не довод в пользу того, что роль исчезла.
if (roleCountsData.value && roleState.value !== null && roleCountsData.value.counts[roleState.value] === undefined)
  roleState.value = null

// Доп. поля тоже грузим до списка — по той же причине, что счётчики.
const { data: customFieldsList } = await useApi<UserCustomFieldData[]>('/user-custom-fields/')

// Не подменяем неудачную загрузку пустым массивом: для диалога `[]` означает «полей нет»
// и он отправит пустую карту, а бэкенд трактует её как полную очистку значений юзера.
const customFields = computed<UserCustomFieldData[] | undefined>(() => customFieldsList.value ?? undefined)

// Фильтровать можно только по списку с опциями, как срезы назначения в GameAddEditDialog:
// у `text` нет справочника значений, а поле без опций дало бы заведомо пустой выбор.
const filterableCustomFields = computed(() => (customFieldsList.value ?? []).filter(
  field => field.type === UserCustomFieldTypeEnum.Select && field.options.length > 0,
))

const selectedCustomField = computed<UserCustomFieldData | null>(
  () => filterableCustomFields.value.find(field => String(field.id) === customFieldState.value) ?? null,
)

// Поле или опцию из cookie могли удалить, а поле — перевести в текст. Бэкенд ответил бы 422
// на весь список, поэтому чиним так же, как роль выше: до первого запроса и мимо сеттеров.
// Поля не доехали — не трогаем.
if (customFieldsList.value && customFieldState.value !== null) {
  if (selectedCustomField.value === null) {
    customFieldState.value = null
    customFieldOptionState.value = null
  }
  else if (customFieldOptionState.value !== null
    && !selectedCustomField.value.options.some(option => String(option.id) === customFieldOptionState.value)) {
    customFieldOptionState.value = null
  }
}

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

// Один набор параметров на список и выгрузку: в файл попадает то же, что на экране.
const listQuery = {
  search: debouncedSearch,
  page,
  itemsPerPage,
  sortBy,
  orderBy,
  role: roleQuery,
  activity: activityQuery,
  activityFrom: activityFromQuery,
  customFieldId: customFieldQuery,
  customFieldOptionId: customFieldOptionQuery,
  lastLogin: lastLoginQuery,
  lastLoginFrom: lastLoginFromQuery,
}

const { data: itemsData, execute: fetchItems } = await useApi<any>(createUrl(`/${table}/`, { query: listQuery }))

const exportUrl = createUrl(`/api/${table}/export`, { query: listQuery })

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)

const { locale } = useI18n({ useScope: 'global' })
const dateLocale = getDateLocale(locale.value)
const datePickerConfig = { enableTime: false, dateFormat: 'Y-m-d', locale: getFlatpickrLocale(locale.value) }

// Роли берём из счётчиков, чтобы селект не предлагал фильтров, заведомо дающих
// пустой список. Бэкенд отдаёт их в порядке PHP-энума — сортируем по алфавиту
// переведённого названия, тем же порядком, что и плашки в шапке.
const roleOptions = computed(() => [
  { value: null, title: t(`${table}-page.all_roles`) },
  ...Object.keys(roleCounts.value.counts)
    .map(name => ({ value: name, title: t(`roles.${name}`) }))
    .sort((a, b) => a.title.localeCompare(b.title, locale.value)),
])

const activityOptions = computed(() => [
  { value: 'played', title: t(`${table}-page.activity_played`) },
  { value: 'not_played', title: t(`${table}-page.activity_not_played`) },
])

const lastLoginOptions = computed(() => [
  { value: LastLoginFilterEnum.Never, title: t(`${table}-page.last_login_never`) },
  { value: LastLoginFilterEnum.Since, title: t(`${table}-page.last_login_since`) },
])

interface Item {
  id: string
  title: string
}
const { data: departmentsList } = await useApi<any>('/departments/all')

let departments: Item[] = [{ title: '', id: '1' }]
if (departmentsList?.value)
  departments = departmentsList.value

const { data: positionsList } = await useApi<any>('/positions/all')

let positions: Item[] = [{ title: '', id: '1' }]
if (positionsList?.value)
  positions = positionsList.value

function findTitleById(items: Item[], id: string): string | undefined {
  const item: any = items.find((i: any) => i.id === id)

  return item?.title
}

const mainRole = (roles: NonEmptyArray<any>) => {
  return roles[0].name
}

const updateList = async (switchLoading: boolean = false) => {
  isLoading.value = useLoading.value
  fetchItems().then(() => {
    isLoading.value = false
  })
  if (switchLoading)
    useLoading.value = !useLoading.value
}

const deleteItem = async () => {
  await $api(`/${table}/${itemForDelete.value.id}`, {
    method: 'DELETE',
  })
  await updateList()
}

const switchActivity = async (item: any) => {
  try {
    await $api(`/${table}/activity`, {
      method: 'PUT',
      body: {
        id: item.id,
      },
    })
  }
  catch (error: any) {
    const data = error?.data ?? error?.response?._data

    if (data?.code !== 'seat_limit_reached')
      throw error

    // Активация не прошла: пользователь остаётся деактивированным, список не меняется.
    notifyError(data.message)

    return
  }
  await updateList()
}

const login = async (item: any) => {
  const res = await $api(`/${table}/login`, {
    method: 'PUT',
    body: {
      id: item.id,
    },
  })

  useCookie('userData').value = null

  // ℹ️ We had to remove abilities in then block because if we don't nav menu items mutation is visible while redirecting user to login page
  // Remove "userAbilities" from cookie
  useCookie('userAbilityRules').value = null
  useCookie('gamesTableOptions').value = null

  // Reset ability to initial ability
  ability.update([])

  useCookie('userAbilityRules').value = res.data.userAbilityRules
  ability.update(res.data.userAbilityRules)

  useCookie('userData').value = res.data.userData
  useCookie('organizationSettings').value = res.data.organizationSettings
  userData.value.avatar = res.data.userData?.avatar
  userStore.hasAvatar = res.data.userData?.avatar
  userStore.fullName = res.data.userData?.fullName
  chatStore.activeChat = null

  await nextTick(() => {
    router.go(0)
  })
}

const message = async (item: any) => {
  await $api(`/chat/create-conversation/${item.id}`, {
    method: 'POST',
  })

  chatStore.userIdAfterRedirect = item.id

  await nextTick(() => {
    router.replace('/users/chat')
  })
}

const deleteConfirmation = async (item: any) => {
  isDeleteConfirmDialogVisible.value = !isDeleteConfirmDialogVisible.value
  itemForDelete.value = item
}

const items = computed(() => itemsData.value?.data)

const total = computed(() => itemsData.value?.total)

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([
  page,
  itemsPerPage,
  sortBy,
  orderBy,
  roleFilter,
  activityFilter,
  activityFromFilter,
  customFieldFilter,
  customFieldOptionFilter,
  lastLoginFilter,
  lastLoginFromFilter,
], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
    role: val[4],
    activity: val[5],
    activityFrom: val[6],
    customFieldId: val[7],
    customFieldOptionId: val[8],
    lastLogin: val[9],
    lastLoginFrom: val[10],
  })
})
</script>

<template>
  <div>
    <UsersRoleSummary
      v-if="roleCountsData"
      v-model="roleFilter"
      class="mb-6"
      :total="roleCounts.total"
      :counts="roleCounts.counts"
    />
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />

        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-upload"
            :href="exportUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t(`${table}-page.export`) }}
          </VBtn>
          <VBtn
            v-if="userIsAdmin(userRoles) || userIsManager(userRoles)"
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-download"
            @click="isImportDialogOpen = !isImportDialogOpen"
          >
            {{ t(`${table}-page.import`) }}
          </VBtn>
          <VBtn
            prepend-icon="tabler-plus"
            @click="isAddDialogOpen = !isAddDialogOpen"
          >
            {{ t(`${table}-page.add`) }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <AppSelect
          v-model="roleFilter"
          hide-details
          :label="t(`${table}-page.filter_role`)"
          :items="roleOptions"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
        <AppSelect
          v-model="activityFilter"
          clearable
          hide-details
          :label="t(`${table}-page.filter_activity`)"
          :items="activityOptions"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
        <AppDateTimePicker
          v-if="activityFilter !== null"
          v-model="activityFromFilter"
          clearable
          hide-details
          :label="t(`${table}-page.filter_activity_from`)"
          :config="datePickerConfig"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
        <AppSelect
          v-if="filterableCustomFields.length > 0"
          v-model="customFieldFilter"
          clearable
          hide-details
          :label="t(`${table}-page.filter_custom_field`)"
          :items="filterableCustomFields"
          item-title="title"
          item-value="id"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
        <AppSelect
          v-if="selectedCustomField !== null"
          v-model="customFieldOptionFilter"
          clearable
          hide-details
          :label="t(`${table}-page.filter_custom_field_value`, { field: selectedCustomField.title })"
          :items="selectedCustomField.options"
          item-title="title"
          item-value="id"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
        <AppSelect
          v-model="lastLoginFilter"
          clearable
          hide-details
          :label="t(`${table}-page.last_login_at`)"
          :items="lastLoginOptions"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
        <AppDateTimePicker
          v-if="isLastLoginSince"
          v-model="lastLoginFromFilter"
          clearable
          hide-details
          :label="t(`${table}-page.filter_last_login_from`)"
          :config="datePickerConfig"
          style="min-inline-size: 200px; max-inline-size: 260px;"
        />
      </VCardText>

      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <template #item.user="{ item }">
            <div class="d-flex align-center">
              <VAvatar
                size="34"
                :variant="!item.profile.has_avatar ? 'tonal' : undefined"
                :color="!item.profile.has_avatar ? resolveUserRoleVariant(mainRole(item.roles)).color : undefined"
                class="me-3"
              >
                <VImg
                  v-if="item.profile.has_avatar"
                  :src="`/api/account/avatar-${item.id}`"
                />
                <span v-else>{{ avatarText(item.profile.name + item.profile.last_name) }}</span>
              </VAvatar>
              <div class="d-flex flex-column">
                <h6 class="text-base">
                  <RouterLink
                    :to="{ name: 'users-view-id', params: { id: item.id } }"
                    class="font-weight-medium text-link"
                  >
                    {{ `${item.profile.name} ${item.profile.last_name}` }}
                  </RouterLink>
                </h6>
                <span class="text-sm text-medium-emphasis">{{ item.email }}</span>
              </div>
            </div>
          </template>
          <template #item.roles="{ item }">
            <div class="d-flex">
              <div
                v-for="role in item.roles"
                :key="role.id"
                class="mr-1"
              >
                <VIcon
                  :size="22"
                  :icon="resolveUserRoleVariant(role.name).icon"
                  :color="resolveUserRoleVariant(role.name).color"
                />
                <VTooltip
                  location="top"
                  activator="parent"
                >
                  {{ t(`roles.${role.name}`) }}
                </VTooltip>
              </div>
            </div>
          </template>
          <template #item.effectiveness="{ item }">
            {{ item.profile.effectiveness }}
          </template>
          <template #item.cases_progress="{ item }">
            {{ t(`${table}-page.cases_progress_value`, { completed: item.cases_completed, assigned: item.cases_assigned }) }}
          </template>
          <template #item.department="{ item }">
            <div class="users-list-truncate-cell">
              {{ findTitleById(departments, item.profile.department_id) }}
              <VTooltip
                v-if="findTitleById(departments, item.profile.department_id)"
                location="top"
                activator="parent"
              >
                {{ findTitleById(departments, item.profile.department_id) }}
              </VTooltip>
            </div>
          </template>
          <template #item.position="{ item }">
            <div class="users-list-truncate-cell">
              {{ findTitleById(positions, item.profile.position_id) }}
              <VTooltip
                v-if="findTitleById(positions, item.profile.position_id)"
                location="top"
                activator="parent"
              >
                {{ findTitleById(positions, item.profile.position_id) }}
              </VTooltip>
            </div>
          </template>
          <template #item.last_login_at="{ item }">
            {{ item.last_login_at ? formatDateToMonthShort(item.last_login_at, true, dateLocale, userTimeZone) : '-' }}
          </template>
          <template #item.status="{ item }">
            <VChip
              :color="resolveUserStatusVariant(resolveUserStatus(item))"
              size="small"
              label
              loading="true"
              class="text-capitalize"
            >
              {{ t(`user-status.${resolveUserStatus(item)}`) }}
            </VChip>
          </template>
          <template #item.actions="{ item }">
            <IconBtn
              v-if="item.roles.find(o => o.name === 'admin') === undefined"
              :to="{ name: 'users-view-id', params: { id: item.id } }"
            >
              <VIcon
                icon="tabler-eye"
                :title="t(`${table}-page.view`)"
              />
            </IconBtn>
            <VBtn
              v-if="item.id !== userData.id"
              icon
              variant="text"
              size="small"
              color="medium-emphasis"
            >
              <VIcon
                size="24"
                icon="tabler-dots-vertical"
              />
              <VMenu activator="parent">
                <VList>
                  <VListItem
                    v-if="item.roles.find(o => o.name === 'admin') === undefined"
                    @click="login(item)"
                  >
                    <template #prepend>
                      <VIcon icon="tabler-login-2" />
                    </template>
                    <VListItemTitle>{{ t(`${table}-page.login`) }}</VListItemTitle>
                  </VListItem>
                  <VListItem @click="message(item)">
                    <template #prepend>
                      <VIcon icon="tabler-message" />
                    </template>
                    <VListItemTitle>{{ t(`${table}-page.message`) }}</VListItemTitle>
                  </VListItem>
                  <VListItem
                    v-if="item.is_active && item.roles.find((o:any) => o.name === 'admin') === undefined"
                    @click="switchActivity(item)"
                  >
                    <template #prepend>
                      <VIcon icon="tabler-lock" />
                    </template>
                    <VListItemTitle>{{ t(`${table}-page.deactivate`) }}</VListItemTitle>
                  </VListItem>
                  <VListItem
                    v-else-if="!item.is_active && item.roles.find((o:any) => o.name === 'admin') === undefined"
                    @click="switchActivity(item)"
                  >
                    <template #prepend>
                      <VIcon icon="tabler-lock-open-2" />
                    </template>
                    <VListItemTitle>{{ t(`${table}-page.activate`) }}</VListItemTitle>
                  </VListItem>
                  <VListItem
                    v-if="ability.can('delete', 'users') && item.roles.find((o:any) => o.name === 'admin') === undefined"
                    @click="deleteConfirmation(item)"
                  >
                    <template #prepend>
                      <VIcon icon="tabler-trash" />
                    </template>
                    <VListItemTitle>{{ t(`${table}-page.delete`) }}</VListItemTitle>
                  </VListItem>
                </VList>
              </VMenu>
            </VBtn>
          </template>
          <template #item.title="{ item }">
            {{ item.title }}
          </template>
          <template #item.skills_count="{ item }">
            {{ item.skills_count }}
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
    <UserImportDialog v-model:isDialogVisible="isImportDialogOpen" />
    <UserAddEditDialog
      v-model:isDialogVisible="isAddDialogOpen"
      :user-data="currenItem"
      :positions="positions"
      :departments="departments"
      :custom-fields="customFields"
      @submit="updateList(false)"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmDialogVisible"
      :confirmation-question="t('confirmation.sure')"
      cancel-msg=""
      :cancel-title="t('confirmation.cancel')"
      :confirm-msg="t('confirmation.deleted')"
      :confirm-title="t('confirmation.success')"
      @confirm="deleteItem"
    />
    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      color="error"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>

<style lang="scss">
.ProseMirror-focused{
  border: none;
}

// Отдел и должность в таблице участников бывают длинными («Территориальный
// менеджер по работе с сетями») и без потолка ширины распирают строку за экран.
// Класс намеренно не generic (text-truncate и т.п.) — файл со стилями не scoped.
.users-list-truncate-cell {
  display: block;
  max-inline-size: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
